import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useActiveChannel } from '@/store/activeChannel';

// Helper: adiciona filtro de channel quando ativo
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function withChannel(query: any, channelId: string | null): any {
  return channelId ? query.eq('channel_id', channelId) : query;
}

// ─── Public types ─────────────────────────────────────────────────────────────

export interface Account {
  id: string;
  name: string;
  type: string;
  institution: string | null;
  balance_cents: number;
  auto_sync_provider: string | null; // 'pluggy' quando conectada via Open Finance
  auto_sync_ref: string | null;
}

export interface MonthlyFlow {
  month: string;
  revenue: number;
  expense: number;
}

export interface DRESummary {
  revenue_gross_cents: number;
  expense_cents: number;
  net_income_cents: number;
  prev_net_income_cents: number;
}

export interface PayableRow {
  id: string;
  payee: string;
  description: string;
  due: string;
  amount_cents: number;
  status: 'pending' | 'overdue' | 'paid' | 'cancelled';
}

export interface ReceivableRow {
  id: string;
  payee: string;
  description: string;
  due: string;
  amount_cents: number;
  status: 'pending' | 'overdue' | 'received' | 'cancelled';
}

export interface CategorySlice {
  name: string;
  value: number;
}

export interface HealthData {
  reconciliation_pct: number;
  reconciled: number;
  total: number;
  uncategorized: number;
  overdue_receivables_cents: number;
  balance_cents: number;
  avg_monthly_expense_cents: number;
  runway_meses: number;
}

export interface TaskRow {
  id: string;
  kind: 'classify' | 'reconcile' | 'approve' | 'review';
  label: string;
  count: number;
}

// ─── Internal row shapes (avoids "never" inference from partial selects) ───────

interface TxFlow   { direction: string; amount_cents: number; occurred_at: string }
interface TxHealth { direction: string; amount_cents: number; occurred_at: string; reconciled: boolean; category_id: string | null }
interface AccBal   { balance_cents: number; type: string }
interface AROverdue { amount_cents: number }

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTHS_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function monthLabel(d: Date): string {
  return `${MONTHS_PT[d.getMonth()]}/${d.getFullYear().toString().slice(2)}`;
}

function startOfMonth(y: number, m: number): Date {
  return new Date(y, m, 1);
}

// ─── Accounts ─────────────────────────────────────────────────────────────────

export function useAccounts() {
  const { tenantId } = useTenant();
  const { activeChannelId } = useActiveChannel();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const cancelRef = useRef(false);

  const refetch = useCallback(() => {
    if (!tenantId) { setLoading(false); return; }
    cancelRef.current = false;
    setLoading(true);
    withChannel(
      supabase
        .from('accounts')
        .select('id, name, type, institution, balance_cents, auto_sync_provider, auto_sync_ref')
        .eq('tenant_id', tenantId)
        .order('name'),
      activeChannelId,
    )
      .then(({ data }: { data: unknown }) => {
        if (cancelRef.current) return;
        setAccounts((data as Account[]) ?? []);
        setLoading(false);
      });
  }, [tenantId, activeChannelId]);

  useEffect(() => {
    refetch();
    return () => { cancelRef.current = true; };
  }, [refetch]);

  return { accounts, loading, refetch };
}

// ─── Cash flow (last 12 months) ───────────────────────────────────────────────

export function useCashFlow12m() {
  const { tenantId } = useTenant();
  const { activeChannelId } = useActiveChannel();
  const [data, setData] = useState<MonthlyFlow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);

    const now = new Date();
    const from = startOfMonth(now.getFullYear(), now.getMonth() - 11);

    withChannel(
      supabase
        .from('transactions')
        .select('direction, amount_cents, occurred_at')
        .eq('tenant_id', tenantId)
        .gte('occurred_at', from.toISOString())
        .order('occurred_at'),
      activeChannelId
    )
      .then(({ data: rows }: { data: unknown; error: unknown }) => {
        const txs = (rows ?? []) as unknown as TxFlow[];
        const map = new Map<string, { revenue: number; expense: number; sort: number }>();

        txs.forEach((r) => {
          const d = new Date(r.occurred_at);
          const key = monthLabel(d);
          const sort = d.getFullYear() * 100 + d.getMonth();
          if (!map.has(key)) map.set(key, { revenue: 0, expense: 0, sort });
          const m = map.get(key)!;
          if (r.direction === 'in') m.revenue += r.amount_cents;
          else m.expense += r.amount_cents;
        });

        setData(
          Array.from(map.entries())
            .sort((a, b) => a[1].sort - b[1].sort)
            .map(([month, v]) => ({ month, revenue: v.revenue, expense: v.expense }))
        );
        setLoading(false);
      });
  }, [tenantId, activeChannelId]);

  return { data, loading };
}

// ─── DRE summary (current + previous period) ──────────────────────────────────

export function useDRESummary(from?: Date, to?: Date) {
  const { tenantId } = useTenant();
  const { activeChannelId } = useActiveChannel();
  const [data, setData] = useState<DRESummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);

    const now = new Date();
    const currStart = from ?? startOfMonth(now.getFullYear(), now.getMonth());
    const currEnd   = to   ?? now;
    const periodMs  = currEnd.getTime() - currStart.getTime();
    const prevStart = new Date(currStart.getTime() - periodMs - 1);

    withChannel(
      supabase
        .from('transactions')
        .select('direction, amount_cents, occurred_at')
        .eq('tenant_id', tenantId)
        .gte('occurred_at', prevStart.toISOString())
        .lte('occurred_at', currEnd.toISOString()),
      activeChannelId
    ).then(({ data: rows }: { data: unknown; error: unknown }) => {
        const txs = (rows ?? []) as unknown as TxFlow[];
        let currRevenue = 0, currExpense = 0, prevRevenue = 0, prevExpense = 0;

        txs.forEach((r) => {
          const d = new Date(r.occurred_at);
          const inCurr = d >= currStart && d <= currEnd;
          if (inCurr) {
            if (r.direction === 'in') currRevenue += r.amount_cents;
            else currExpense += r.amount_cents;
          } else {
            if (r.direction === 'in') prevRevenue += r.amount_cents;
            else prevExpense += r.amount_cents;
          }
        });

        setData({
          revenue_gross_cents: currRevenue,
          expense_cents: currExpense,
          net_income_cents: currRevenue - currExpense,
          prev_net_income_cents: prevRevenue - prevExpense,
        });
        setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, activeChannelId, from?.toISOString(), to?.toISOString()]);

  return { data, loading };
}

// ─── Revenue / expense by category ────────────────────────────────────────────

export function useRevenueExpenseByCategory() {
  const { tenantId } = useTenant();
  const { activeChannelId } = useActiveChannel();
  const [revenue, setRevenue] = useState<CategorySlice[]>([]);
  const [expense, setExpense] = useState<CategorySlice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);

    const now = new Date();
    const monthStart = startOfMonth(now.getFullYear(), now.getMonth());

    withChannel(
      supabase
        .from('transactions')
        .select('direction, amount_cents, category_id, category:categories(name)')
        .eq('tenant_id', tenantId)
        .gte('occurred_at', monthStart.toISOString()),
      activeChannelId
    ).then(({ data: rows }: { data: unknown; error: unknown }) => {
        const txs = (rows ?? []) as unknown as Array<{ direction: string; amount_cents: number; category: { name: string } | null }>;
        const revMap = new Map<string, number>();
        const expMap = new Map<string, number>();

        txs.forEach((r) => {
          const name = r.category?.name ?? 'Sem categoria';
          if (r.direction === 'in') revMap.set(name, (revMap.get(name) ?? 0) + r.amount_cents);
          else expMap.set(name, (expMap.get(name) ?? 0) + r.amount_cents);
        });

        setRevenue(Array.from(revMap.entries()).map(([name, value]) => ({ name, value })));
        setExpense(Array.from(expMap.entries()).map(([name, value]) => ({ name, value })));
        setLoading(false);
      });
  }, [tenantId, activeChannelId]);

  return { revenue, expense, loading };
}

// ─── Upcoming payables ────────────────────────────────────────────────────────

export function useUpcomingPayables() {
  const { tenantId } = useTenant();
  const { activeChannelId } = useActiveChannel();
  const [data, setData] = useState<PayableRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);

    const next30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

    withChannel(
      supabase
        .from('accounts_payable')
        .select('id, description, amount_cents, due_date, status, payee:payees(name)')
        .eq('tenant_id', tenantId)
        .lte('due_date', next30)
        .in('status', ['pending', 'overdue'])
        .order('due_date'),
      activeChannelId
    ).then(({ data: rows }: { data: unknown; error: unknown }) => {
        const ap = (rows ?? []) as unknown as Array<{
          id: string; description: string; amount_cents: number; due_date: string;
          status: PayableRow['status']; payee: { name: string } | null;
        }>;
        setData(ap.map((r) => ({
          id: r.id, payee: r.payee?.name ?? '—', description: r.description,
          due: r.due_date, amount_cents: r.amount_cents, status: r.status,
        })));
        setLoading(false);
      });
  }, [tenantId, activeChannelId]);

  return { data, loading };
}

// ─── Upcoming receivables ─────────────────────────────────────────────────────

export function useUpcomingReceivables() {
  const { tenantId } = useTenant();
  const { activeChannelId } = useActiveChannel();
  const [data, setData] = useState<ReceivableRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);

    const next30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

    withChannel(
      supabase
        .from('accounts_receivable')
        .select('id, description, amount_cents, due_date, status, payee:payees(name)')
        .eq('tenant_id', tenantId)
        .lte('due_date', next30)
        .in('status', ['pending', 'overdue'])
        .order('due_date'),
      activeChannelId
    ).then(({ data: rows }: { data: unknown; error: unknown }) => {
        const ar = (rows ?? []) as unknown as Array<{
          id: string; description: string; amount_cents: number; due_date: string;
          status: ReceivableRow['status']; payee: { name: string } | null;
        }>;
        setData(ar.map((r) => ({
          id: r.id, payee: r.payee?.name ?? '—', description: r.description,
          due: r.due_date, amount_cents: r.amount_cents, status: r.status,
        })));
        setLoading(false);
      });
  }, [tenantId, activeChannelId]);

  return { data, loading };
}

// ─── Health metrics ───────────────────────────────────────────────────────────

export function useHealthMetrics() {
  const { tenantId } = useTenant();
  const { activeChannelId } = useActiveChannel();
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);

    Promise.all([
      withChannel(
        supabase
          .from('transactions')
          .select('reconciled, category_id, direction, amount_cents, occurred_at')
          .eq('tenant_id', tenantId),
        activeChannelId,
      ),
      withChannel(
        supabase
          .from('accounts')
          .select('balance_cents, type')
          .eq('tenant_id', tenantId),
        activeChannelId,
      ),
      withChannel(
        supabase
          .from('accounts_receivable')
          .select('amount_cents')
          .eq('tenant_id', tenantId)
          .eq('status', 'overdue'),
        activeChannelId,
      ),
    ]).then(([txRes, accRes, arRes]) => {
      const txs = (txRes.data ?? []) as unknown as TxHealth[];
      const accs = (accRes.data ?? []) as unknown as AccBal[];
      const overdueAR = (arRes.data ?? []) as unknown as AROverdue[];

      const total = txs.length;
      const reconciled = txs.filter((t) => t.reconciled).length;
      const uncategorized = txs.filter((t) => !t.category_id).length;
      // Verificado empiricamente (Sandbox Pluggy): o balance de cartão de
      // crédito já chega com sinal (negativo = fatura em aberto). Soma simples
      // preserva a semântica da API — nenhum ajuste de sinal aqui.
      const balanceCents = accs.reduce((s, a) => s + a.balance_cents, 0);
      const overdueReceivablesCents = overdueAR.reduce((s, r) => s + r.amount_cents, 0);

      const now = new Date();
      const threeMonthsAgo = startOfMonth(now.getFullYear(), now.getMonth() - 3);
      const recentExpenses = txs
        .filter((t) => t.direction === 'out' && new Date(t.occurred_at) >= threeMonthsAgo)
        .reduce((s, t) => s + t.amount_cents, 0);
      const avgMonthlyExpense = recentExpenses / 3;

      setData({
        reconciliation_pct: total > 0 ? reconciled / total : 0,
        reconciled,
        total,
        uncategorized,
        overdue_receivables_cents: overdueReceivablesCents,
        balance_cents: balanceCents,
        avg_monthly_expense_cents: avgMonthlyExpense,
        runway_meses: avgMonthlyExpense > 0 ? balanceCents / avgMonthlyExpense : 0,
      });
      setLoading(false);
    });
  }, [tenantId, activeChannelId]);

  return { data, loading };
}

// ─── Recent transactions ──────────────────────────────────────────────────────

export interface TransactionRow {
  id: string;
  description: string;
  direction: 'in' | 'out';
  amount_cents: number;
  occurred_at: string;
  reconciled: boolean;
  account_name: string;
  category_id: string | undefined;
}

export function useRecentTransactions(limit = 100, from?: Date, to?: Date) {
  const { tenantId } = useTenant();
  const { activeChannelId } = useActiveChannel();
  const [data, setData] = useState<TransactionRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(() => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    let q = supabase
      .from('transactions')
      .select('id, description, direction, amount_cents, occurred_at, reconciled, category_id, account:accounts(name)')
      .eq('tenant_id', tenantId)
      .order('occurred_at', { ascending: false })
      .limit(limit);
    if (from) q = q.gte('occurred_at', from.toISOString());
    if (to)   q = q.lte('occurred_at', to.toISOString());
    withChannel(q, activeChannelId)
      .then(({ data: rows }: { data: unknown; error: unknown }) => {
        const txs = (rows ?? []) as unknown as Array<{
          id: string; description: string; direction: string;
          amount_cents: number; occurred_at: string; reconciled: boolean;
          category_id: string | null;
          account: { name: string } | null;
        }>;
        setData(txs.map((r) => ({
          id: r.id,
          description: r.description,
          direction: r.direction as 'in' | 'out',
          amount_cents: r.amount_cents,
          occurred_at: r.occurred_at,
          reconciled: r.reconciled,
          category_id: r.category_id ?? undefined,
          account_name: r.account?.name ?? '—',
        })));
        setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, limit, activeChannelId, from?.toISOString(), to?.toISOString()]);

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, refetch: fetch };
}

// ─── Operator tasks ───────────────────────────────────────────────────────────

export function useOperatorTasks() {
  const { tenantId } = useTenant();
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);

    Promise.all([
      supabase
        .from('transactions')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', tenantId)
        .is('category_id', null),
      supabase
        .from('transactions')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', tenantId)
        .eq('reconciled', false),
      supabase
        .from('accounts_payable')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', tenantId)
        .eq('status', 'pending'),
    ]).then(([uncat, unrec, pendPay]) => {
      const result: TaskRow[] = [];

      if ((uncat.count ?? 0) > 0) {
        result.push({ id: 't-classify', kind: 'classify', label: `${uncat.count} transações sem categoria`, count: uncat.count ?? 0 });
      }
      if ((unrec.count ?? 0) > 0) {
        result.push({ id: 't-reconcile', kind: 'reconcile', label: `${unrec.count} transações não conciliadas`, count: unrec.count ?? 0 });
      }
      if ((pendPay.count ?? 0) > 0) {
        result.push({ id: 't-approve', kind: 'approve', label: `${pendPay.count} pagamentos aguardando aprovação`, count: pendPay.count ?? 0 });
      }

      setTasks(result);
      setLoading(false);
    });
  }, [tenantId]);

  return { tasks, loading };
}
